import React, { useMemo } from 'react'
import { ConfigDescription } from '@/helpers/widget'
import { SearchDropdown } from '@/components/ui-primitives/search-dropdown'
import {
  Checkbox,
  CheckboxLabel,
  FormField,
  Input,
  Label, NumberInput,
  RadioGroup,
  Select,
  TextArrayInput,
} from '@/components/ui-primitives/small'
import { FormFieldProps } from '@/components/FormField'
import { TablePPCustomConfigView } from '@/components/widgets/TablePlusPlus/components/CustomConfiguration'
import { DEFAULT_TABLEPP_CUSTOM_CONFIG } from '@/components/widgets/TablePlusPlus/config-schema'
import { FilterPlusPlusConfig } from '@/components/widgets/FilterPlusPlus/FilterPlusPlusConfig'
import { SetComponentConfig } from '@/components/widgets/ComponentWidget/SetComponentConfig'
import { useDialog } from '@/hooks/use-dialog'
import { HelpDialog } from '@/components/HelpDialog'
import { CodeEditor, JsonCodeEditor } from '@/components/editor/code-editor'
import { useFloatingState } from '@/components/editor/panels/panel-registry'
import { WidgetConfig } from '@/components/WidgetConfiguration'
import { useWidgetSelectorStore } from '@/components/editor/widget-selector/widget-selector'
import { findWidgetIdForPath } from '@/components/editor/widget-dom'
import { assertNever } from '@/utils/assert-never'
import { mergeClasses } from '@/helpers/styling'
import { FieldFeedback } from '@/components/editor/widget-editor/editor-controller'
import { Textarea } from '@/components/ui-primitives/text-area'
import { CollapsibleSection } from '@/components/editor/widget-editor/collapsible-section'
import { useAlwaysShowAdvanced } from '@/components/editor/local-settings'
import { create } from 'zustand'

export type AnyConfigDesc = ConfigDescription<any, any, any>

/** What the descriptor-driven fields of one widget read from and write to */
export type WidgetFieldsForm = {
  configDraft: WidgetConfig
  updateField: (name: string, value: unknown) => void
  localHistoryKeyPrefix: string | undefined
  feedback: Record<string, FieldFeedback>
  setFeedback: (field: string, kind: 'valid' | 'warning' | 'error', message?: React.ReactNode) => void
}

/** Show or Hide pressed in the selected widget's settings; dropped when another widget is selected */
const useAdvancedForWidget = create<{ show: boolean }>()(() => ({ show: false }))

useWidgetSelectorStore.subscribe((state, prev) => {
  if (state.selectedPath !== prev.selectedPath) useAdvancedForWidget.setState({ show: false })
})

function useShowAdvanced() {
  const always = useAlwaysShowAdvanced()
  return useAdvancedForWidget(s => s.show) || always
}

function setShowAdvanced(show: boolean) {
  useAdvancedForWidget.setState({ show })
}

/** The options to show: all of them, or without the `advanced` ones while those are hidden */
function useVisibleVariables(variables: ReadonlyArray<AnyConfigDesc>) {
  const showAdvanced = useShowAdvanced()
  return useMemo(
    () => showAdvanced ? variables : variables.filter(v => !v.advanced),
    [ variables, showAdvanced ]
  )
}

/** Fields for a widget descriptor's variables, with `formGroup`s rendered as fieldsets. */
export function VariableFields(props: {
  form: WidgetFieldsForm
  variables: ReadonlyArray<AnyConfigDesc>
  overrides?: (variable: AnyConfigDesc) => Partial<FormFieldProps>
}) {
  const { form, overrides } = props
  const visible = useVisibleVariables(props.variables)
  return (
    <>
      {createFormParts(visible).map((part) => {
        switch (part.type) {
          case "item":
            return renderVariableField(part.item, form, overrides?.(part.item))
          case "group":
            return (
              <fieldset key={`fs-${part.legend}`} className="flex flex-col border border-border rounded px-2 pt-1 pb-2 gap-2">
                <legend className="px-1 mb-0.5 text-xs text-muted-foreground w-auto select-none">{part.legend}</legend>
                {part.items.map(v => renderVariableField(v, form, overrides?.(v)))}
              </fieldset>
            )
          default:
            return assertNever(part)
        }
      })}
    </>
  )
}

/** A collapsible group of options; left out entirely when hiding advanced options leaves none. */
export function SettingsSection(props: {
  id: string
  header: string
  form: WidgetFieldsForm
  variables: ReadonlyArray<AnyConfigDesc>
  defaultExpanded?: boolean
}) {
  const visible = useVisibleVariables(props.variables)
  if (!visible.length) return null
  return (
    <CollapsibleSection id={props.id}
                        header={props.header}
                        defaultExpanded={props.defaultExpanded}>
      <VariableFields form={props.form} variables={props.variables} />
    </CollapsibleSection>
  )
}

/** Shows or hides a tab's advanced options, for the selected widget only; left out when the tab has none or they always show */
export function AdvancedOptionsToggle(props: {
  variables: ReadonlyArray<Pick<AnyConfigDesc, 'advanced'>>
  className?: string
}) {
  const always = useAlwaysShowAdvanced()
  const showAdvanced = useShowAdvanced()
  const count = props.variables.filter(v => v.advanced).length
  if (!count || always) return null
  const options = `${count} advanced ${count === 1 ? "option" : "options"}`
  return (
    <button type="button"
            className={mergeClasses("self-start text-xs text-muted-foreground hover:text-foreground", props.className)}
            title="For this widget only"
            onClick={() => setShowAdvanced(!showAdvanced)}>
      {showAdvanced
        ? <>Showing {options} · <span className="underline">Hide</span></>
        : <>{options} hidden · <span className="underline">Show</span></>}
    </button>
  )
}

type FormItemPart = { type: "item", item: AnyConfigDesc }
type FormGroupPart = { type: "group", legend: string, items: AnyConfigDesc[] }
type FormPart = FormItemPart | FormGroupPart

function createFormParts(variables: ReadonlyArray<AnyConfigDesc>): FormPart[] {
  const parts: FormPart[] = []
  const groupsByLegend = new Map<string, FormGroupPart>()
  for (const v of variables) {
    if (v.formGroup) {
      if (!groupsByLegend.has(v.formGroup)) {
        const group: FormGroupPart = { type: "group", legend: v.formGroup, items: [] }
        groupsByLegend.set(v.formGroup, group)
        parts.push(group)
      }
      groupsByLegend.get(v.formGroup)!.items.push(v)
    } else {
      parts.push({ type: "item", item: v })
    }
  }
  return parts
}

function renderVariableField(
  variable: AnyConfigDesc,
  form: WidgetFieldsForm,
  overrides?: Partial<FormFieldProps>,
) {
  const { configDraft, updateField, localHistoryKeyPrefix } = form
  const feedback = form.feedback[variable.name]
  return (
    <React.Fragment key={variable.name}>
      <FormFieldSwitch
        name={variable.name}
        label={variable.label}
        type={variable.type}
        inputVariant={variable.inputVariant}
        inlineLabel={variable.inlineLabel}
        showOptionIconsOnly={variable.showOptionIconsOnly}
        topicValueInterpolationEnabled={'topicValueInterpolationEnabled' in variable && variable.topicValueInterpolationEnabled}
        helpComponent={'helpComponent' in variable ? variable.helpComponent : undefined}
        options={'options' in variable ? variable.options : undefined}
        placeholder={variable.placeholder}
        noResultsLabel={variable.noResultsLabel}
        value={(configDraft as any)[variable.name]}
        onChange={updateField}
        defaultValue={variable.defaultValue}
        setConfigFeedback={(kind, message) => form.setFeedback(variable.name, kind, message)}
        localHistoryKey={localHistoryKeyPrefix && `${localHistoryKeyPrefix}:${variable.name}`}
        {...overrides}
      />
      {feedback && (typeof feedback.message === 'string' ? (
        <div role={feedback.kind === 'error' ? 'alert' : undefined}
             className={mergeClasses("rounded-md px-2.5 py-1.5 text-xs", FEEDBACK_STYLE[feedback.kind])}>
          {feedback.message}
        </div>
      ) : feedback.message /* already a styled report, e.g. Table++'s alerts */)}
    </React.Fragment>
  )
}

const FEEDBACK_STYLE = {
  error: "bg-[#fde8e8] text-[#9b1c1c]",
  warning: "bg-[#fff4dc] text-[#6b4600]",
}

export function FormFieldSwitch(props: FormFieldProps) {
  const id = `${props.name}-formfield`
  const isDocked = useFloatingState() === 'docked'

  const formFieldClassName =
    props.inlineLabel ? 'flex-row items-center' : ''

  const defaultLabel = (
    <LabelWithHelp label={props.label}
                   helpComponent={props.helpComponent}
                   topicValueInterpolationEnabled={props.topicValueInterpolationEnabled}
                   htmlFor={id}
                   className={props.inlineLabel
                     ? 'whitespace-nowrap text-ellipsis overflow-hidden'
                     : ''} />
  )

  switch (props.type) {
    case 'select': {
      if (props.inputVariant === 'radiogroup') {
        return (
          <FormField inputId={id} className={formFieldClassName}>
            {defaultLabel}
            <RadioGroup
              value={props.value || String(props.defaultValue) || ''}
              onChange={value => props.onChange(props.name, value)}
              options={props.options || []}
              iconsOnly={Boolean(props.showOptionIconsOnly)}
            />
          </FormField>
        )
      } else {
        return (
          <FormField inputId={id} className={formFieldClassName}>
            {defaultLabel}
            <Select
              name={props.name}
              value={props.value || String(props.defaultValue) || ''}
              onChange={e => props.onChange(props.name, e.currentTarget.value)}>
              {props.options && props.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>
        )
      }
    }
    case 'searchselect':
      return (
        <FormField inputId={id}>
          {defaultLabel}
          <SearchDropdown
            id={id}
            name={props.name}
            value={props.value ?? null}
            onChange={value => props.onChange(props.name, value || '')}
            options={props.options}
            labelConfig={{
              placeholder: props.placeholder || 'Search...',
              noOptionsFound: props.noResultsLabel || 'No options found.',
            }}
            classNames={{
              input: '!px-1 h-8 text-[13px] shadow-none rounded-sm',
              inputWrapper: 'bg-white0 rounded-md border-border',
              item: 'text-xs',
            }} />
        </FormField>
      )
    case 'boolean':
      return (
        <FormField inputId={id} className="flex-row">
          <CheckboxLabel>
            <Checkbox
              name={props.name}
              checked={props.value || false}
              onClick={() => props.onChange(props.name, !props.value)} />
            {props.label}
          </CheckboxLabel>
        </FormField>
      )
    case 'array':
      return (
        <FormField inputId={id}>
          {defaultLabel}
          <TextArrayInput
            id={id}
            name={props.name}
            value={props.value}
            onChange={value => props.onChange(props.name, value)} />
        </FormField>
      )
    case 'string': /* DEBT: some have 'string' not 'text' */
    case 'text':
      return (
        <FormField inputId={id} className={formFieldClassName}>
          {defaultLabel}
          <Input
            type="text"
            name={props.name}
            value={props.value}
            placeholder={props.placeholder}
            onChange={e => props.onChange(props.name, e.currentTarget.value)}
          />
        </FormField>
      )
    case 'textarea':
      return (
        <FormField inputId={id}>
          {defaultLabel}
          <Textarea
            id={id}
            name={props.name}
            value={props.value ?? ''}
            placeholder={props.placeholder}
            onChange={e => props.onChange(props.name, e.currentTarget.value)}
            className="min-h-[60px] resize-y border-border bg-white0 px-3 py-1.5 text-[13px] md:text-[13px] [field-sizing:content] focus-visible:ring-[#51479e]/40 focus-visible:ring-offset-0"
          />
        </FormField>
      )
    case 'number':
      return (
        <FormField inputId={id} className={formFieldClassName}>
          {defaultLabel}
          <NumberInput
            name={props.name}
            value={props.value
              // previously it had wrong type (string) so this "migrates" it
              ? parseFloat(String(props.value))
              : null}
            onChange={value => props.onChange(props.name, value)}
          />
        </FormField>
      )
    case 'yasgui':
      return (
        <FormField inputId={id}>
          {defaultLabel}
          <CodeEditor mode="sparql"
                      showGutter={isDocked}
                      name={props.name}
                      minHeight={props.editorMinHeight}
                      onRun={props.onRun}
                      value={props.value || ''}
                      onValueChange={value => {
                        props.onChange(props.name, value)
                      }} />
        </FormField>
      )
    case 'json':
      return (
        <FormField inputId={id}>
          {defaultLabel}
          <JsonCodeEditor name={props.name}
                      showGutter={isDocked}
                      value={props.value}
                      onValueChange={value => {
                        props.onChange(props.name, value)
                      }} />
        </FormField>
      )
    case "json-text":
      return (
        <FormField>
          {defaultLabel}
          <CodeEditor mode="json"
                      showGutter={isDocked}
                      name={props.name}
                      value={props.value || ""}
                      onValueChange={value => {
                        props.onChange(props.name, value as string)
                      }} />
        </FormField>
      )
    case "html":
      return (
        <FormField inputId={id}>
          {defaultLabel}
          <CodeEditor mode="html"
                      showGutter={isDocked}
                      name={props.name}
                      minHeight="360px"
                      value={props.value ?? ''}
                      onValueChange={value => props.onChange(props.name, value)}
                      localHistoryKey={props.localHistoryKey} />
        </FormField>
      )
    case "turtle":
      return (
        <FormField inputId={id}>
          {defaultLabel}
          <CodeEditor mode="turtle"
                      showGutter={isDocked}
                      name={props.name}
                      value={props.value || ''}
                      onValueChange={value => {
                        props.onChange(props.name, value)
                      }} />
        </FormField>
      )
    case "tablepp-custom":
      return (
        <FormField>
          <TablePPCustomConfigView name={props.name}
                                   customConfig={props.value ?? DEFAULT_TABLEPP_CUSTOM_CONFIG}
                                   setConfigFeedback={props.setConfigFeedback}
                                   onChange={props.onChange} />
        </FormField>
      )
    case "filterpp-custom":
      return (
        <FormField>
          {defaultLabel}
          <FilterPlusPlusConfig customConfig={props.value}
                                onChange={v => props.onChange(props.name, v)} />
        </FormField>
      )
    case "component-config":
      return (
        <FormField>
          {defaultLabel}
          <SetComponentConfig value={props.value}
                              onChange={value => props.onChange(props.name, value)} />
        </FormField>
      )
  }

  // descriptors in plain JS can still name a type that has no editor
  return <div>No editor implemented yet for: {(props as { type: string }).type}</div>
}

export function LabelWithHelp(
  props: {
    htmlFor?: string
    className?: string
  } & Pick<FormFieldProps, 'label' | 'topicValueInterpolationEnabled' | 'helpComponent'>
) {
  const show = useDialog()
  const helpComponent = props.helpComponent
  const selectedPath = useWidgetSelectorStore(s => s.selectedPath)

  return (
    <Label htmlFor={props.htmlFor ?? undefined} className={props.className}>
        <div className="flex-grow text-ellipsis overflow-hidden">{props.label}</div>
        {props.topicValueInterpolationEnabled &&(
          <i className="text-gray-700 truncate">topic {"{{variables}}"} enabled</i>
        )}
        {helpComponent && (
          <a href="#" onClick={e => {
            e.preventDefault()
            void show(HelpDialog, {
              component: helpComponent,
              widgetId: findWidgetIdForPath(selectedPath),
            })
          }}>
            Help...
          </a>
        )}
    </Label>
  )
}
